<template>
    <div class="financing-filter-container">

        <!-- Navbar with Filter Icon -->
        <nav class="navbar filterbar" :class="enableFilters ? '' : ' filterbar--dropdown'" role="filter" aria-label="dropdown filter">
            <div class="navbar-start">
                <div class="navbar-item" v-if="enableFilters">
                    <button class="filter-toggle-btn" @click="toggleFilterPanel" aria-label="Open filters">
                        <span class="icon">
                            <!-- SVG filter icon -->
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 5h18M6 12h12M10 19h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </span>
                        <span class="filter-label">{{ translations.filters || 'Filters' }}</span>
                    </button>
                </div>
                <div class="navbar-item" v-else>
                    <div class="select">
                        <select v-model="selectedMeasure">
                            <option :value="0">{{ translations['select-first-option'] }}</option>
                            <option v-for="item in measures" :value="item.id">{{ item.title }}</option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="navbar-end" v-if="enableFilters">
                <div class="navbar-item">
                    <div class="results-info">
                        <span class="results-count">{{ filteredFinanceItems.length }} {{ translations.results || 'resultaten' }}</span>
                    </div>
                </div>
            </div>
        </nav>

        <!-- Results Section -->

        <main class="results-section">
            <div class="results-container">
                <!-- Filter Slide-Out Panel -->
                <transition name="slideout" v-if="enableFilters">
                    <aside v-if="filterPanelOpen" class="filter-sidebar slideout-panel" ref="slideoutPanel">

                        <div class="filter-header">
                            <h3>{{ translations.filters || 'Filters' }}</h3>
                            <button
                                v-if="hasActiveFilters"
                                @click="clearAllFilters"
                                class="button is-small is-text clear-filters-btn"
                            >
                                {{ translations.clearAll || 'Wis alle filters' }}
                            </button>
                            <button class="close-panel-btn" @click="toggleFilterPanel" aria-label="Sluit filters">
                                <span class="icon">&times;</span>
                            </button>
                        </div>

                        <!-- Active Filters Display -->
                        <div v-if="hasActiveFilters" class="active-filters-section">
                            <h4 class="filter-title">{{ translations.activeFilters || 'Actieve filters' }}</h4>
                            <div class="active-filters">
                        <span v-for="filter in getActiveFiltersList()" :key="filter.key" class="tag is-info">
                            {{ filter.label }}
                            <button @click="removeFilter(filter.category, filter.key)" class="delete is-small"></button>
                        </span>
                            </div>
                        </div>

                        <!-- Geographic Level Filter -->
                        <div class="filter-section">
                            <h4 class="filter-title">
                                {{ translations.geographicLevel || 'Geografisch niveau' }}
                                <span v-if="selectedGeographicCount > 0"> ({{ selectedGeographicCount }})</span>
                            </h4>
                            <div class="filter-group">
                                <label class="checkbox-item">
                                    <input type="checkbox" v-model="filters.geographic.national" />
                                    <span>{{ translations.national || 'Landelijk' }}</span>
                                    <span class="result-count" v-if="showCounts">({{ geographicCounts.national }})</span>
                                </label>
                                <label class="checkbox-item">
                                    <input type="checkbox" v-model="filters.geographic.provincial" />
                                    <span>{{ translations.provincial || 'Provinciaal' }}</span>
                                    <span class="result-count" v-if="showCounts">({{ geographicCounts.provincial }})</span>
                                </label>
                                <label class="checkbox-item">
                                    <input type="checkbox" v-model="filters.geographic.municipal" />
                                    <span>{{ translations.municipal || 'Gemeentelijk' }}</span>
                                    <span class="result-count" v-if="showCounts">({{ geographicCounts.municipal }})</span>
                                </label>
                            </div>
                        </div>

                        <!-- Usage Function Filter -->
                        <div class="filter-section">
                            <h4 class="filter-title">
                                {{ translations.residentialType || 'Gebruiksfunctie' }}
                                <span v-if="selectedResidentialTypeCount > 0"> ({{ selectedResidentialTypeCount }})</span>
                            </h4>
                            <div class="filter-group">
                                <label class="radio-item">
                                    <input type="radio" v-model="filters.residentialType" value="residential" />
                                    <span>{{ translations.residential || 'Woonhuis' }}</span>
                                </label>
                                <label class="radio-item">
                                    <input type="radio" v-model="filters.residentialType" value="non-residential" />
                                    <span>{{ translations.nonResidential || 'Niet-woonhuis' }}</span>
                                </label>
                                <label class="radio-item">
                                    <input type="radio" v-model="filters.residentialType" value="" />
                                    <span>{{ translations.both || 'Beide' }}</span>
                                </label>
                            </div>
                        </div>

                        <!-- Customer Type Filter -->
                        <div class="filter-section">
                            <h4 class="filter-title">
                                {{ translations.customerType || 'Klant type' }}
                                <span v-if="selectedCustomerTypeCount > 0"> ({{ selectedCustomerTypeCount }})</span>
                            </h4>
                            <div class="filter-group">
                                <label class="radio-item">
                                    <input type="radio" v-model="filters.customerType" value="particulier" />
                                    <span>{{ translations.particulier || 'Particulier' }}</span>
                                </label>
                                <label class="radio-item">
                                    <input type="radio" v-model="filters.customerType" value="zakelijk" />
                                    <span>{{ translations.zakelijk || 'Zakelijk' }}</span>
                                </label>
                                <label class="radio-item">
                                    <input type="radio" v-model="filters.customerType" value="" />
                                    <span>{{ translations.both || 'Beide' }}</span>
                                </label>
                            </div>
                        </div>

                        <!-- Monument Type Filter -->
                        <div class="filter-section">
                            <h4 class="filter-title">
                                {{ translations.monumentType || 'Monument type' }}
                                <span v-if="selectedMonumentTypesCount > 0"> ({{ selectedMonumentTypesCount }})</span>
                            </h4>
                            <div class="filter-group">
                                <label class="checkbox-item" v-for="type in monumentTypes" :key="type.id">
                                    <input type="checkbox" v-model="filters.monumentTypes" :value="type.id" />
                                    <span>{{ type.title }}</span>
                                    <span class="result-count" v-if="showCounts">({{ monumentTypeCounts[type.id] }})</span>
                                </label>
                            </div>
                        </div>

                        <!-- Measures Filter (Direct List with Search) -->
                        <div class="filter-section">
                            <h4 class="filter-title">
                                {{ translations.measures || 'Maatregelen' }}
                                <span v-if="selectedMeasuresCount > 0"> ({{ selectedMeasuresCount }})</span>
                            </h4>
                            <div class="measures-list-wrapper">
                                <div class="measures-list">
                                    <div class="dropdown" :class="{ 'is-active': measuresDropdownOpen }">
                                        <div class="dropdown-trigger">
                                            <button class="button" type="button" @click="measuresDropdownOpen = !measuresDropdownOpen">
                                                <span>{{ translations.measures || 'Maatregelen' }}</span>
                                                <span class="icon is-small">
                                                    <i class="icofont-thin-down" :class="{ 'fa-rotate-180': measuresDropdownOpen }"></i>
                                                </span>
                                            </button>
                                        </div>
                                        <div class="dropdown-menu" role="menu" v-if="measuresDropdownOpen">
                                            <div v-if="measures.length > 10" class="measures-searchbar">
                                                <input
                                                    type="text"
                                                    v-model="measuresSearchQuery"
                                                    class="input"
                                                    :placeholder="translations.searchMeasures || 'Zoek maatregelen'"
                                                />
                                            </div>
                                            <div class="dropdown-content">
                                                <label v-for="option in filteredMeasuresList" :key="option.id" class="dropdown-item">
                                                    <input type="checkbox" v-model="filters.selectedMeasures" :value="option.id"  />
                                                    <span>{{ option.title }}</span>
                                                    <span class="result-count" v-if="showCounts">({{ measureCounts[option.id] }})</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="filteredMeasuresList.length === 0" class="no-measures-found">
                                        <span>{{ translations.noMeasuresFound || 'Geen maatregelen gevonden' }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="filter-actions" ref="filterActions">
                            <button class="show-results-btn button"  @click="toggleFilterPanel" aria-label="Close filters">
                                <span class="results-count">{{ filteredFinanceItems.length }} {{ translations.results || 'resultaten' }}</span>
                            </button>
                        </div>
                    </aside>
                </transition>
                <!-- Overlay when filter panel is open -->
                <div v-if="filterPanelOpen && enableFilters" class="filter-overlay" @click="toggleFilterPanel"></div>
                <div class="finance-content-wrapper">
                    <div class="results-header" v-if="enableFilters">
                        <h2>{{ translations.financingOptions || 'Financieringsmogelijkheden' }}</h2>
                    </div>

                    <!-- No Results Message -->
                    <div v-if="filteredFinanceItems.length === 0" class="no-results">
                        <div class="notification is-warning">
                            <h3>{{ translations.noResults || 'Geen resultaten gevonden' }}</h3>
                            <p>{{ translations.noResultsMessage || 'Probeer enkele filters te wissen om meer resultaten te krijgen.' }}</p>
                            <button @click="clearAllFilters" class="button is-primary">
                                {{ translations.clearFilters || 'Wis filters' }}
                            </button>
                        </div>
                    </div>

                    <!-- Finance Items (match original content structure) -->
                    <div v-for="item in filteredFinanceItems" class="finance-wrapper">
                        <div :id="'financing-' + item.id" class="finance-wrapper">
                            <div class="finance-title" :ref="'financing' + item.id">
                                <h2>{{ item.title }}</h2>
                                <p>{{ translations['by']}}: {{ item.who }}</p>
                            </div>

                            <div class="finance-content">
                                <div class="columns">
                                    <div class="column is-2">
                                        <div v-if="item.authorityImgUrl" class="finance-logo">
                                            <img :src="item.authorityImgUrl" alt="" />
                                        </div>
                                    </div>
                                    <div class="column-container">
                                        <div class="column is-5">
                                            <h3>{{ translations.description }}</h3>
                                            <div v-html="item.description"></div>

                                            <h3>{{ translations.amount }}</h3>
                                            <div v-html="item.amount"></div>
                                        </div>

                                        <div class="column is-5" v-if="showConditions(item)">
                                            <h3>{{ translations.conditions }}</h3>
                                            <div v-html="item.conditions"></div>
                                        </div>
                                        <div class="column is-5">
                                            <div v-for="button in item.buttons">
                                                <a :href="button.url" class="button is-primary-light has-arrow">{{ button.name }}</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
export default {
    props: {
        enableFilters: {
            type: Boolean,
            default: false
        },
        measures: {
            required: true
        },
        financings: {
            required: true
        },
        monumentTypes: {
            type: Array,
            default: () => []
        },
        translations: {
            required: true
        },
        parentRegionUrl: {
            type: String,
            default: null,
        },
        parentRegionName: {
            type: String,
            default: null,
        },
    },
    data() {
        let filterPanelOpen = true;
        const width = window.innerWidth;
        if (width <= 480) {
            filterPanelOpen = false;
        }

        return {
            showCounts: true,
            measuresDropdownOpen: false,
            filters: {
                geographic: {
                    national: true,  // Default to showing national
                    provincial: false,
                    municipal: false
                },
                residentialType: '', // '', 'residential', 'non-residential'
                customerType: '', // '', 'particulier', 'zakelijk'
                monumentTypes: [], // Array of monument type IDs
                selectedMeasures: [] // Array of measure IDs
            },
            measuresSearchQuery: '', // Search query for measures
            filterPanelOpen: filterPanelOpen,
            geographicOptions: [
                { id: 'national', title: this.translations.national || 'Landelijk' },
                { id: 'provincial', title: this.translations.provincial || 'Provinciaal' },
                { id: 'municipal', title: this.translations.municipal || 'Gemeentelijk' },
            ],
            financeItems: {},
            selectedMeasure: 0,
        }
    },
    computed: {
        filteredFinanceItems() {
            if (this.enableFilters) {
                
                return this.financings.filter(item => this.passesFilters(item));
            } else {
                return this.financeItems;
            }
        },
        hasActiveFilters() {
            return this.filters.geographic.national ||
                   this.filters.geographic.provincial ||
                   this.filters.geographic.municipal ||
                   this.filters.residentialType !== '' ||
                   this.filters.customerType !== '' ||
                   this.filters.monumentTypes.length > 0 ||
                   this.filters.selectedMeasures.length > 0;
        },
        filterMeasures() {
            // return all without empty titless
            return this.measures.filter(measure => measure.title);
        },
        filteredMeasuresList() {
            let list = this.filterMeasures;
            if (this.measures.length > 10 && this.measuresSearchQuery) {
                const query = this.measuresSearchQuery.toLowerCase();
                list = list.filter(measure => measure.title && measure.title.toLowerCase().includes(query));
            }
            return list;
        },
        geographicCounts() {
            if (!this.showCounts || !this.enableFilters) return {};
            return {
                national: this.financings.filter(item => item.geographicLevel === 'national').length,
                provincial: this.financings.filter(item => item.geographicLevel === 'provincial').length,
                municipal: this.financings.filter(item => item.geographicLevel === 'municipal').length
            };
        },
        monumentTypeCounts() {
            if (!this.showCounts) return {};
            const counts = {};
            this.monumentTypes.forEach(type => {
                counts[type.id] = this.filteredFinanceItems.filter(item => Array.isArray(item.buildingStates) && item.buildingStates.includes(type.id)).length;
            });
            return counts;
        },
        measureCounts() {
            if (!this.showCounts) return {};
            const counts = {};
            this.filteredMeasuresList.forEach(measure => {
                counts[measure.id] = this.filteredFinanceItems.filter(item => Array.isArray(item.measures) && item.measures.some(m => m.id === measure.id)).length;
            });
            return counts;
        },
        selectedGeographicCount() {
            // Count selected geographic filters (national, provincial, municipal)
            return Object.values(this.filters.geographic).filter(Boolean).length;
        },
        selectedResidentialTypeCount() {
            // Only count if a type is selected
            return this.filters.residentialType ? 1 : 0;
        },
        selectedCustomerTypeCount() {
            // Only count if a type is selected
            return this.filters.customerType ? 1 : 0;
        },
        selectedMonumentTypesCount() {
            // Count selected monument types
            return this.filters.monumentTypes.length;
        },
        selectedMeasuresCount() {
            // Count selected measures
            return this.filters.selectedMeasures.length;
        },
    },
    watch: {
        filters: {
            handler: function() {
                this.saveFiltersToLocalStorage();
            },
            deep: true
        },
        selectedMeasure: function(value) {
            localStorage.setItem('dgm.filter.measure', value)

            if (value === 0) {
                this.reset();
            } else {
                this.filter()
            }
        }
    },
    methods: {
        filter: function() {
            /* filtering by looping through all the financings */
            this.financeItems = this.financings.filter(el => {
                if (el.hasOwnProperty('measures')) {
                    return el.measures.some(i => {
                        return i.id===this.selectedMeasure;
                    })
                }
                return false
            });
        },
        getLocalStorageValue: function(key) {
            if (localStorage.getItem(key)) {
                return localStorage.getItem(key)
            }
            return null
        },

        reset: function() {
            this.financeItems = this.financings
        },

        passesFilters(item) {
            // Geographic Level Filter (OR logic within category)
            const geographicMatch = this.checkGeographicMatch(item);

            // Usage Function Filter
            const residentialTypeMatch = this.checkResidentialTypeMatch(item);

            // Customer Type Filter
            const customerTypeMatch = this.checkCustomerTypeMatch(item);

            // Monument Types Filter (OR logic within category)
            const monumentTypeMatch = this.checkMonumentTypeMatch(item);

            // Measures Filter (OR logic within category)
            const measuresMatch = this.checkMeasuresMatch(item);

            // AND logic between categories
            return geographicMatch && residentialTypeMatch && customerTypeMatch && monumentTypeMatch && measuresMatch;
        },

        checkGeographicMatch(item) {
            const geo = this.filters.geographic;

            // If no geographic filters selected, show all
            if (!geo.national && !geo.provincial && !geo.municipal) {
                return true;
            }

            // Check if item matches any selected geographic levels
            if (geo.national && item.geographicLevel === 'national') return true;
            if (geo.provincial && item.geographicLevel === 'provincial') return true;
            if (geo.municipal && item.geographicLevel === 'municipal') return true;

            return false;
        },

        checkResidentialTypeMatch(item) {
            if (!this.filters.residentialType) return true;

            // If item doesn't have residentialType property, it applies to both
            if (!item.hasOwnProperty('residentialType') || !item.residentialType) return true;

            return item.residentialType === this.filters.residentialType ||
                   item.residentialType === 'both';
        },

        checkCustomerTypeMatch(item) {
            if (!this.filters.customerType) return true;

            // If item doesn't have customerType property, it applies to both
            if (!item.hasOwnProperty('customerType') || !item.customerType) return true;

            return item.customerType === this.filters.customerType;
        },

        checkMonumentTypeMatch(item) {
            if (this.filters.monumentTypes.length === 0) return true;

            if (!item.hasOwnProperty('buildingStates') || !item.buildingStates) return false;

            // Check if item has any of the selected monument types
            return this.filters.monumentTypes.some(selectedType =>
                item.buildingStates.includes(selectedType)
            );
        },

        checkMeasuresMatch(item) {
            if (this.filters.selectedMeasures.length === 0) return true;

            if (!item.hasOwnProperty('measures') || !item.measures) return false;

            // Check if item supports any of the selected measures
            return this.filters.selectedMeasures.some(selectedMeasureId =>
                item.measures.some(measure => measure.id === selectedMeasureId)
            );
        },

        clearAllFilters() {
            this.filters = {
                geographic: {
                    national: true,
                    provincial: false,
                    municipal: false
                },
                residentialType: '',
                customerType: '',
                monumentTypes: [],
                selectedMeasures: []
            };
            this.checkGeographicCounts();
        },

        removeFilter(category, key) {
            switch(category) {
                case 'geographic':
                    this.filters.geographic[key] = false;
                    break;
                case 'residentialType':
                    this.filters.residentialType = '';
                    break;
                case 'customerType':
                    this.filters.customerType = '';
                    break;
                case 'monumentTypes':
                    const typeIndex = this.filters.monumentTypes.indexOf(key);
                    if (typeIndex > -1) {
                        this.filters.monumentTypes.splice(typeIndex, 1);
                    }
                    break;
                case 'measures':
                    const measureIndex = this.filters.selectedMeasures.indexOf(key);
                    if (measureIndex > -1) {
                        this.filters.selectedMeasures.splice(measureIndex, 1);
                    }
                    break;
            }
        },

        getActiveFiltersList() {
            const activeFilters = [];

            // Geographic filters
            Object.entries(this.filters.geographic).forEach(([key, value]) => {
                if (value) {
                    activeFilters.push({
                        category: 'geographic',
                        key: key,
                        label: this.getGeographicLabel(key)
                    });
                }
            });

            // Usage function filter
            if (this.filters.residentialType) {
                activeFilters.push({
                    category: 'residentialType',
                    key: this.filters.residentialType,
                    label: this.getResidentialTypeLabel(this.filters.residentialType)
                });
            }

            // Customer type filter
            if (this.filters.customerType) {
                activeFilters.push({
                    category: 'customerType',
                    key: this.filters.customerType,
                    label: this.getCustomerTypeLabel(this.filters.customerType)
                });
            }

            // Monument types filters
            this.filters.monumentTypes.forEach(typeId => {
                const type = this.monumentTypes.find(t => t.id === typeId);
                if (type) {
                    activeFilters.push({
                        category: 'monumentTypes',
                        key: typeId,
                        label: type.title
                    });
                }
            });

            // Measures filters
            this.filters.selectedMeasures.forEach(measureId => {
                const measure = this.measures.find(m => m.id === measureId);
                if (measure) {
                    activeFilters.push({
                        category: 'measures',
                        key: measureId,
                        label: measure.title
                    });
                }
            });

            return activeFilters;
        },

        getGeographicLabel(level) {
            const labels = {
                national: this.translations.national || 'Landelijk',
                provincial: this.translations.provincial || 'Provinciaal',
                municipal: this.translations.municipal || 'Gemeentelijk'
            };
            return labels[level] || level;
        },

        getResidentialTypeLabel(func) {
            const labels = {
                residential: this.translations.residential || 'Woonhuis',
                'non-residential': this.translations.nonResidential || 'Niet-woonhuis'
            };
            return labels[func] || func;
        },

        getCustomerTypeLabel(type) {
            const labels = {
                particulier: this.translations.particulier || 'Particulier',
                zakelijk: this.translations.zakelijk || 'Zakelijk'
            };
            return labels[type] || type;
        },

        getItemMonumentTypes(item) {
            if (!item.monumentTypes || !Array.isArray(item.monumentTypes)) return [];

            return item.monumentTypes.map(typeId => {
                const type = this.monumentTypes.find(t => t.id === typeId);
                return type ? type.title : typeId;
            });
        },

        getItemMeasures(item) {
            if (!item.measures || !Array.isArray(item.measures)) return [];
            return item.measures;
        },

        getSelectedMeasuresText() {
            return this.filters.selectedMeasures.map(measureId => {
                const measure = this.measures.find(m => m.id === measureId);
                return measure ? measure.title : measureId;
            });
        },

        saveFiltersToLocalStorage() {
            localStorage.setItem('dgm.filters', JSON.stringify(this.filters));
        },

        loadFiltersFromLocalStorage() {
            const saved = localStorage.getItem('dgm.filters');
            if (saved) {
                try {
                    this.filters = { ...this.filters, ...JSON.parse(saved) };
                } catch (e) {
                    console.warn('Could not parse saved filters from localStorage');
                }
            }
        },

        scrollTo(financingId) {
            const vm = this;
            setTimeout(function() {
                const element = vm.$refs[financingId];
                if (element && element[0]) {
                    const top = element[0].offsetTop;
                    window.scrollTo(0, top);
                }
            }, 100);
        },
        toggleFilterPanel() {
            this.filterPanelOpen = !this.filterPanelOpen;
            this.$nextTick(() => {
                this.showCounts = this.filterPanelOpen;
                this.adjustResultsHeightToSlideout();
            });
        },
        adjustResultsHeightToSlideout() {
            // Only on desktop
            if (window.innerWidth > 480 && this.filterPanelOpen) {
                // Use ref to get slideoutPanel element
                const slideout = this.$refs.slideoutPanel;
                const results = this.$el.querySelector('.results-section');
                document.documentElement.classList.remove('no-scroll');
                
                if (slideout && results) {
                    // Use scrollHeight for real content height
                    const slideoutHeight = slideout.scrollHeight;
                    results.style.minHeight = slideoutHeight + 'px';
                }
            } else {
                // Reset minHeight on mobile or when closed
                document.documentElement.classList.toggle('no-scroll', this.filterPanelOpen);
                setTimeout(() => {
                    const results = this.$el.querySelector('.results-section');
                    if (results) results.style.minHeight = '';
                }, 300);
            }
        },
        handleResizeAdjustResultsHeight() {
            this.adjustResultsHeightToSlideout();
        },
        handleEscClose(e) {
            if (this.filterPanelOpen && e.key === 'Escape') {
                this.filterPanelOpen = false;
            }
        },
        checkGeographicCounts() {
            if (this.enableFilters && this.geographicCounts.national === 0) {
                this.filters.geographic.national = false;
                if (this.geographicCounts.provincial > 0) {
                    this.filters.geographic.provincial = true;
                } else if (this.geographicCounts.municipal > 0) {
                    this.filters.geographic.municipal = true;
                }
            }
        },
        showConditions(item) {
            return item.conditions && item.conditions.trim() !== '' && item.conditions.trim() !== '<p></p>';
        },

        clearFiltersBeforeNavigate() {
            localStorage.removeItem('dgm.filters');
            localStorage.removeItem('dgm.filter.measure');
        }
    },
    mounted() {
        // Load saved filters
        this.loadFiltersFromLocalStorage();
        this.financeItems = this.financings
        this.checkGeographicCounts();

        // Handle scrolling to specific item from URL hash
        let uri = window.location.href.split('#');
        if (uri.length === 2) {
            this.scrollTo(uri[1]);
        } else if (!this.enableFilters) {
            this.selectedMeasure = Number(this.getLocalStorageValue('dgm.filter.measure'));
        }

        if (this.enableFilters) {
            // Close dropdown when clicking outside
            this.handleOutsideClick = (e) => {
                if (!this.$el.contains(e.target)) {
                    this.measuresDropdownOpen = false;
                }
            };

            document.addEventListener('click', this.handleOutsideClick);
            document.addEventListener('keydown', this.handleEscClose);
            window.addEventListener('resize', this.handleResizeAdjustResultsHeight);
            this.$watch('filterPanelOpen', (open) => {
                this.$nextTick(() => {
                    this.handleResizeAdjustResultsHeight();
                });
            });

            window.addEventListener('resize', this.handleResizeAdjustResultsHeight);
            this.$watch('filterPanelOpen', () => {
                this.$nextTick(() => {
                    this.handleResizeAdjustResultsHeight();
                });
            });
        }
    },
    beforeDestroy() {
        // Clean up event listener
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleEscClose);
        window.removeEventListener('resize', this.handleResizeAdjustResultsHeight);
    }
}
</script>